import { ReactTFC } from "../../../../../utils/fp/missing-ts-parts";

import { css, SerializedStyles } from "@emotion/react";

import React, { useState, type HTMLInputAutoCompleteAttribute } from "react";
import { fontColor, FONTS, fontSize } from "../../../../../styles/fonts";
import { CSS_COLORS } from "../../../../../styles/styles-css-variables";
import { DownArrowSvg } from "../../../../../assets";
import { nonEmpty } from "@swo/shared";

type SwarovskiDropdownProps = {
  name: string;
  selected: { value: string; label: string } | null;
  options: Array<{ value: string; label: string }>;
  onChange: (v: string) => void;
  attachStyles?: SerializedStyles | Array<SerializedStyles>;
  disabled?: boolean;
  required?: boolean;
  emptyLabel: string;
  onBlur?: () => void;
  onFocus?: () => void;
  autoComplete?: HTMLInputAutoCompleteAttribute | undefined;
};

export const SwarovskiDropdownWithShortLabel: ReactTFC<SwarovskiDropdownProps & { children: React.ReactNode }> = ({
  attachStyles,
  ...props
}) => {
  return (
    <div
      css={css`
        position: relative;
        border-style: solid;
        border-width: 1px;
        border-color: transparent;
        background-color: transparent;

        &:focus-within {
          outline: 2px dotted ${CSS_COLORS.Green};
        }
        cursor: pointer !important;

        height: 24px;

        margin: 0;
        margin-left: 8px;
        margin-right: 0;

        padding-left: 6px;
        padding-right: 6px;

        border-radius: 8px;

        display: flex;
        align-items: center;
        justify-content: center;
      `}
    >
      <SwarovskiDropdown
        {...props}
        attachStyles={[
          ...(Array.isArray(attachStyles) ? attachStyles : [attachStyles]),
          css`
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;

            opacity: 0;
          `,
        ].filter(nonEmpty)}
      />

      <div
        role={"button"}
        tabIndex={-1}
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
        `}
      >
        <span css={[FONTS.SwarovskiFutura300, fontColor.black, fontSize.x2]}>{props.children}</span>

        <DownArrowSvg
          css={css`
            width: 12px;
            margin-left: -4px;
          `}
        />
      </div>
    </div>
  );
};

export const SwarovskiDropdown: ReactTFC<SwarovskiDropdownProps> = props => {
  return (
    <select
      required={props.required}
      disabled={props.disabled}
      name={props.name}
      value={props.selected ? props.selected.value : ""}
      onChange={e => {
        e.preventDefault();

        const { value } = e.target;

        props.onChange(value);
      }}
      onBlur={props.onBlur}
      onFocus={props.onFocus}
      css={[
        FONTS.SwarovskiFutura300,
        props.attachStyles,
        css`
          border: none;
          border-bottom: 1px solid var(--so-green);
          font-size: inherit;
        `,
      ]}
    >
      <option hidden disabled value={""}>
        {props.emptyLabel}
      </option>

      {props.options.map(option => {
        return (
          <option key={option.value} value={option.value} disabled={props.disabled}>
            {option.label}
          </option>
        );
      })}
    </select>
  );
};

export const SwarovskiDropdown2: ReactTFC<SwarovskiDropdownProps> = props => {
  return (
    <select
      required={props.required}
      disabled={props.disabled}
      name={props.name}
      value={props.selected ? props.selected.value : ""}
      onChange={e => {
        const { value } = e.target;

        props.onChange(value);
      }}
      onBlur={props.onBlur}
      onFocus={props.onFocus}
      autoComplete={props.autoComplete}
      css={[
        FONTS.SwarovskiFutura300,
        props.attachStyles,
        css`
          border: none;
          -webkit-appearance: none;

          &:-webkit-autofill,
          &:-webkit-autofill:focus {
            transition:
              background-color 0s 600000s,
              color 0s 600000s !important;
          }
        `,
      ]}
    >
      <option hidden disabled value={""}>
        {props.emptyLabel}
      </option>

      {props.options.map(option => {
        return (
          <option key={option.value} value={option.value} disabled={props.disabled}>
            {option.label}
          </option>
        );
      })}
    </select>
  );
};
